// Input — the single-line text-field primitive, pure.
//
// Rebuilt from foundations v1 against the design vision (.claude/research/design/06).
// The register is calm/near-monochrome: a quiet field carried by a
// translucent ink tint (NOT a resting border and NOT a fill on
// --color-bg-elevated, which equals --color-input-bg on the white canvas and so
// would be invisible). The edge is revealed by interaction — a focus ring
// on :focus-visible — which is the border→elevation discipline for a
// control (§2/§5). Placeholder text sits on --color-fg-faint so it still
// clears the contrast floor.
//
// `invalid` is a genuine binary validity STATE (not a meaning-swapping mode
// flag), so a boolean is the honest shape here. It never leans on colour
// alone (SC 1.4.1): it pairs the danger hue with a warning glyph (a shape)
// AND a distinct bottom underline (a structural change), and it sets
// aria-invalid so assistive tech is told programmatically.
//
// Pure/props-driven: `size` picks the density and `invalid` picks the
// state; every other input attribute (value, onChange, placeholder, type,
// aria-*, id…) passes straight through to the <input>. The component owns
// no state and performs no I/O — all changes are reported UP by the
// consumer's own handlers. `type` is intentionally NOT defaulted so
// "text" (native default), "search", "email", … all pass through cleanly.
//
// No intrinsic visible text lives here (label/placeholder/aria-label are
// the consumer's, via props), so there is no Lingui macro to author — the
// only glyph is decorative (aria-hidden).

import type { InputHTMLAttributes, Ref } from "react";
import { Icon } from "../Icon/Icon.js";
import "./Input.css";

type InputSize = "sm" | "md";

// The native `size` attribute (a number: visible character width) is
// dropped and re-typed as the density token — the design API wins the name.
export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & {
    readonly size?: InputSize;
    readonly invalid?: boolean;
    // Opt-in handle on the underlying <input>, so a dialog can put the focus
    // there when it opens (the Textarea's `inputRef`, same reason).
    readonly inputRef?: Ref<HTMLInputElement>;
};

export const Input = ({
    size = "md",
    invalid = false,
    className,
    inputRef,
    ...rest
}: InputProps) => (
    <span className={`bilda-input-wrap${className ? ` ${className}` : ""}`}>
        <input
            ref={inputRef}
            className={`bilda-input bilda-input--${size}${
                invalid ? " bilda-input--invalid" : ""
            }`}
            {...rest}
            aria-invalid={invalid || undefined}
        />
        {invalid ? (
            <span className="bilda-input__invalid-icon" aria-hidden="true">
                <Icon name="warning" />
            </span>
        ) : null}
    </span>
);
